import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { PadButton } from '../emulator/types';

export type ZoneKind = 'button' | 'dpad' | 'stick';

export interface Zone {
  id: string;
  kind: ZoneKind;
  /** kind === 'button' のとき押されるボタン */
  button?: PadButton;
  el: HTMLElement;
  rect: DOMRect;
  /** 当たり判定を見た目より広げる比率（指は太いので 1.15 程度が快適） */
  slop: number;
  /** kind === 'stick': 倒した方向を十字キーとしても出す */
  asDpad?: boolean;
  /**
   * kind === 'stick': 指を置いた場所を中心にする。
   * 固定位置だと画面を見ずに中心を探す必要があるが、
   * フローティングなら「触れた所が中心」になるので狙いを外さない。
   */
  floating?: boolean;
  /** kind === 'stick': 最大まで倒したとみなす半径（px）。floating で使う。 */
  radius?: number;
}

/** スティックの現在値。ノブの描画にも使う。 */
export interface StickState {
  zoneId: string;
  /** -1..1 に正規化した傾き */
  x: number;
  y: number;
  /** 中心のビューポート座標（floating では指を置いた位置） */
  originX: number;
  originY: number;
}

export interface TouchOutput {
  buttons: Set<PadButton>;
  sticks: StickState[];
}

interface PointerState {
  zoneId: string | null;
  x: number;
  y: number;
  /** floating スティックで指を置いた位置 */
  originX: number;
  originY: number;
}

/** スティックが方向キーを出し始める傾き。小さすぎると意図しない入力が出る。 */
const STICK_DPAD_THRESHOLD = 0.4;
/** 斜め入力に入る角度の幅（度）。広いほど斜めを出しやすい。 */
const DIAGONAL_WINDOW = 30;

/**
 * バーチャルパッドの多点タッチ処理。
 *
 * iPhone Safari で「効くパッド」にするための要点をここに集約している:
 *
 * 1. Touch Events ではなく Pointer Events を使う。
 *    マウス/ペン/タッチが同じコードで動き、PC でも同じ実装が使える。
 *
 * 2. setPointerCapture は使わない。
 *    キャプチャすると指が別ボタンへ滑った時に元要素へイベントが固定され、
 *    「A から B へスライドして押し替える」操作 (ローリング) ができなくなる。
 *    代わりにルート要素で全ポインタを受け、自前の矩形当たり判定で解決する。
 *
 * 3. rect はイベント毎に取り直さずキャッシュする。
 *    getBoundingClientRect() を pointermove 毎に呼ぶとレイアウト再計算が走り、
 *    実機で目に見えてカクつく。resize / orientationchange 時だけ更新する。
 *
 * 4. touch-action: none と preventDefault の併用。
 *    どちらか片方だけだと iOS でスクロール・ダブルタップズーム・
 *    テキスト選択・コールアウトのいずれかが必ず残る。
 */
export function useMultiTouch(onChange: (out: TouchOutput) => void) {
  const zones = useRef(new Map<string, Zone>());
  const pointers = useRef(new Map<number, PointerState>());
  const rootRef = useRef<HTMLDivElement | null>(null);
  const lastSerialized = useRef('');

  const measureAll = useCallback(() => {
    for (const zone of zones.current.values()) {
      zone.rect = zone.el.getBoundingClientRect();
    }
  }, []);

  const register = useCallback(
    (zone: Omit<Zone, 'rect'>) => {
      zones.current.set(zone.id, { ...zone, rect: zone.el.getBoundingClientRect() });
      return () => {
        zones.current.delete(zone.id);
      };
    },
    [],
  );

  /** 座標からゾーンを引く。ボタンは円形、D-Pad/スティックは矩形で判定する。 */
  const hitTest = useCallback((x: number, y: number): Zone | null => {
    let best: Zone | null = null;
    let bestDist = Infinity;

    for (const zone of zones.current.values()) {
      const r = zone.rect;
      if (r.width === 0 || r.height === 0) continue;

      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const halfW = (r.width / 2) * zone.slop;
      const halfH = (r.height / 2) * zone.slop;

      const dx = x - cx;
      const dy = y - cy;

      const inside =
        zone.kind === 'button'
          ? (dx * dx) / (halfW * halfW) + (dy * dy) / (halfH * halfH) <= 1
          : Math.abs(dx) <= halfW && Math.abs(dy) <= halfH;

      if (!inside) continue;

      // 判定が重なった場合は中心が近い方を採用する
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        best = zone;
      }
    }
    return best;
  }, []);

  const recompute = useCallback(() => {
    const buttons = new Set<PadButton>();
    const sticks: StickState[] = [];

    for (const p of pointers.current.values()) {
      if (!p.zoneId) continue;
      const zone = zones.current.get(p.zoneId);
      if (!zone) continue;

      if (zone.kind === 'button' && zone.button) {
        buttons.add(zone.button);
        continue;
      }

      const r = zone.rect;

      if (zone.kind === 'dpad') {
        const nx = ((p.x - r.left) / r.width) * 2 - 1; // -1..1
        const ny = ((p.y - r.top) / r.height) * 2 - 1;

        // 8 方向。中心の不感帯を設けて「押していないのに反応する」のを防ぐ。
        const dead = 0.22;
        const mag = Math.hypot(nx, ny);
        if (mag < dead) continue;
        // 斜めを出しやすくするため、軸に対する角度で判定する
        const angle = (Math.atan2(ny, nx) * 180) / Math.PI; // -180..180
        const oct = Math.round(((angle + 360) % 360) / 45) % 8;
        // 0=右 1=右下 2=下 3=左下 4=左 5=左上 6=上 7=右上
        if (oct === 0 || oct === 1 || oct === 7) buttons.add('RIGHT');
        if (oct === 3 || oct === 4 || oct === 5) buttons.add('LEFT');
        if (oct === 1 || oct === 2 || oct === 3) buttons.add('DOWN');
        if (oct === 5 || oct === 6 || oct === 7) buttons.add('UP');
        continue;
      }

      // --- スティック ---
      // floating では指を置いた位置が中心。固定ではゾーンの中心。
      const originX = zone.floating ? p.originX : r.left + r.width / 2;
      const originY = zone.floating ? p.originY : r.top + r.height / 2;
      const radius = zone.floating ? (zone.radius ?? 60) : Math.min(r.width, r.height) / 2;

      let nx = (p.x - originX) / radius;
      let ny = (p.y - originY) / radius;

      // 円の外へは出さない（斜めが 1.41 倍強くならないように）
      const mag = Math.hypot(nx, ny);
      if (mag > 1) {
        nx /= mag;
        ny /= mag;
      }

      sticks.push({ zoneId: zone.id, x: nx, y: ny, originX, originY });

      if (zone.asDpad && mag >= STICK_DPAD_THRESHOLD) {
        // 角度から 8 方向へ落とす。斜めの窓を広めに取って
        // 「斜めに入れたいのに片方しか出ない」を防ぐ。
        const angle = ((Math.atan2(ny, nx) * 180) / Math.PI + 360) % 360;
        const near = (target: number) => {
          const d = Math.abs(((angle - target + 540) % 360) - 180);
          return d <= 45 + DIAGONAL_WINDOW / 2;
        };
        if (near(0)) buttons.add('RIGHT');
        if (near(180)) buttons.add('LEFT');
        if (near(90)) buttons.add('DOWN');
        if (near(270)) buttons.add('UP');
      }
    }

    // 変化がなければ通知しない（毎 pointermove で再レンダリングさせない）
    const key =
      [...buttons].sort().join(',') +
      '|' +
      sticks.map((s) => `${s.zoneId}:${s.x.toFixed(2)},${s.y.toFixed(2)}`).join(';');
    if (key === lastSerialized.current) return;
    lastSerialized.current = key;
    onChange({ buttons, sticks });
  }, [onChange]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const onDown = (e: PointerEvent) => {
      // マウス右クリック等は無視
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const zone = hitTest(e.clientX, e.clientY);
      if (!zone) return;
      e.preventDefault();
      // floating スティックでは、この「指を置いた位置」が以後の中心になる
      pointers.current.set(e.pointerId, {
        zoneId: zone.id,
        x: e.clientX,
        y: e.clientY,
        originX: e.clientX,
        originY: e.clientY,
      });
      recompute();
      // 触覚フィードバック（Android Chrome のみ有効。iOS は無視される）
      navigator.vibrate?.(8);
    };

    const onMove = (e: PointerEvent) => {
      const p = pointers.current.get(e.pointerId);
      if (!p) return;
      e.preventDefault();

      // coalesced events を使うと高頻度サンプリング環境でも取りこぼさない
      const last = e.getCoalescedEvents?.().at(-1) ?? e;
      p.x = last.clientX;
      p.y = last.clientY;

      const zone = hitTest(p.x, p.y);
      // D-Pad / スティックから指が外れても、そのゾーンに留まらせる方が操作しやすい。
      // ボタン間はスライドで押し替えられるようにする。
      const currentZone = p.zoneId ? zones.current.get(p.zoneId) : null;
      if (zone) {
        p.zoneId = zone.id;
      } else if (currentZone && currentZone.kind === 'button') {
        p.zoneId = null;
      }
      recompute();
    };

    const onUp = (e: PointerEvent) => {
      if (!pointers.current.delete(e.pointerId)) return;
      recompute();
    };

    // passive: false でないと preventDefault が効かない
    const opt = { passive: false } as AddEventListenerOptions;
    root.addEventListener('pointerdown', onDown, opt);
    root.addEventListener('pointermove', onMove, opt);
    root.addEventListener('pointerup', onUp, opt);
    root.addEventListener('pointercancel', onUp, opt);
    // iOS でアプリ切り替え等が起きた時に押しっぱなしを残さない
    window.addEventListener('blur', () => {
      pointers.current.clear();
      recompute();
    });

    // 拡大鏡・コンテキストメニューの抑止
    const noop = (e: Event) => e.preventDefault();
    root.addEventListener('contextmenu', noop);
    root.addEventListener('selectstart', noop);

    return () => {
      root.removeEventListener('pointerdown', onDown, opt);
      root.removeEventListener('pointermove', onMove, opt);
      root.removeEventListener('pointerup', onUp, opt);
      root.removeEventListener('pointercancel', onUp, opt);
      root.removeEventListener('contextmenu', noop);
      root.removeEventListener('selectstart', noop);
    };
  }, [hitTest, recompute]);

  // レイアウト変化で rect を取り直す
  useEffect(() => {
    measureAll();
    const ro = new ResizeObserver(() => measureAll());
    if (rootRef.current) ro.observe(rootRef.current);
    window.addEventListener('resize', measureAll);
    window.addEventListener('orientationchange', measureAll);
    // フルスクリーン遷移直後はレイアウトが安定するまで 1 フレーム待つ
    const onFs = () => requestAnimationFrame(measureAll);
    document.addEventListener('fullscreenchange', onFs);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureAll);
      window.removeEventListener('orientationchange', measureAll);
      document.removeEventListener('fullscreenchange', onFs);
    };
  }, [measureAll]);

  return useMemo(() => ({ rootRef, register, measureAll }), [register, measureAll]);
}
